'use client';

import React from 'react';
import { useAuth } from 'src/context/auth';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faBullseyeArrow,
  faMapLocationDot,
  faClipboardList,
} from '@fortawesome/pro-solid-svg-icons';

export type EmptyReason =
  | 'noStrategies'
  | 'selectStrategy'
  | 'noTerritory'
  | 'selectTerritory';

export interface StrategyCenterEmptyStateProps {
  reason: EmptyReason;
}

const StrategyCenterEmptyState = ({
  reason,
}: StrategyCenterEmptyStateProps) => {
  const { user } = useAuth();
  const managers = user?.company?.managers;
  const firstManager = managers?.[0];

  const content: Record<
    EmptyReason,
    { icon: typeof faBullseyeArrow; title: string; description: string }
  > = {
    noStrategies: {
      icon: faBullseyeArrow,
      title: 'No strategies available',
      description: firstManager
        ? `Contact your manager ${firstManager.first_name} ${firstManager.last_name} to get started.`
        : 'Contact your manager to get started.',
    },
    selectStrategy: {
      icon: faClipboardList,
      title: 'Select a Strategy',
      description:
        'Choose a strategy from the dropdown above to start viewing opportunities and insights.',
    },
    noTerritory: {
      icon: faMapLocationDot,
      title: 'No primary territory assigned',
      description: firstManager
        ? `Contact your manager ${firstManager.first_name} ${firstManager.last_name} to have a primary territory assigned.`
        : 'Contact your manager to have a primary territory assigned.',
    },
    selectTerritory: {
      icon: faMapLocationDot,
      title: 'Select a Territory',
      description:
        'Choose a territory from the dropdown above to view opportunities specific to that region.',
    },
  };

  const { icon, title, description } = content[reason];

  return (
    <div
      data-testid="strategy-center-empty-state"
      className="h-screen pb-1 w-full bg-level-1 flex flex-col overflow-hidden"
    >
      <main
        data-testid="strategy-center-empty-state-body"
        className="flex-1 flex flex-col gap-4 min-h-0"
      >
        <section
          data-testid="strategy-center-empty-state-content"
          className="flex-1 flex flex-col items-center justify-center bg-neutral-050 rounded-lg shadow shadow-neutral-050 border border-neutral-200 overflow-hidden min-h-0"
          role="status"
          aria-labelledby="empty-state-title"
          aria-describedby="empty-state-description"
        >
          <div className="flex flex-col items-center gap-3 text-center px-4">
            <div
              className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-50"
              aria-hidden="true"
            >
              <Icon icon={icon} size="3x" className="text-secondary" />
            </div>
            <div className="flex flex-col gap-1">
              <h1
                id="empty-state-title"
                data-testid="strategy-center-empty-state-title"
                className="text-2xl font-semibold text-primary"
              >
                {title}
              </h1>
              <p
                id="empty-state-description"
                data-testid="strategy-center-empty-state-description"
                className="text-base text-tertiary max-w-sm"
              >
                {description}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default StrategyCenterEmptyState;
